import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Plus, Loader2, ArrowLeft, Copy, RefreshCw, Link as LinkIcon,
  CheckCircle2, XCircle, MessageCircleQuestion, Clock, Award, Users, Trash2,
  ChevronRight, ChevronLeft, Star,
} from "lucide-react";
import api from "@/lib/api";
import { AdminModal } from "@/components/AdminModal";
import { Header } from "./Schools";
import { fmtDate } from "@/lib/date";

function StatusBadge({ status }) {
  const map = {
    pending: { label: "Pending", cls: "bg-amber-100 text-amber-700", icon: Clock },
    approved: { label: "Approved", cls: "bg-emerald-100 text-emerald-700", icon: CheckCircle2 },
    rejected: { label: "Rejected", cls: "bg-rose-100 text-rose-700", icon: XCircle },
    changes_requested: { label: "Changes requested", cls: "bg-amber-100 text-amber-700", icon: MessageCircleQuestion },
  };
  const m = map[status] || map.pending;
  const Icon = m.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${m.cls}`}>
      <Icon className="w-3 h-3" /> {m.label}
    </span>
  );
}

// Converts a <input type="date"> value ("YYYY-MM-DD") to an ISO string representing
// LOCAL midnight of that day, not UTC midnight. Passing the string directly to `new Date()`
// parses it as UTC midnight regardless of the browser's timezone — for anyone east of UTC
// (e.g. IST, UTC+5:30), picking "today" during the first ~5.5 hours of the local day can
// silently produce a timestamp that's still in the future in UTC terms, which the backend's
// (correct) datetime comparison then reports as "Registration has not opened yet" even
// though the admin genuinely meant "right now." Building the Date from explicit Y/M/D
// components uses the browser's own local timezone to resolve midnight correctly.
const toLocalMidnightISO = (dateString) => {
  if (!dateString) return "";
  return dateString.slice(0, 10);
};

const EMPTY_FORM = {
  training_type: "standard",
  booking_id: "", trainer_name: "", training_topic: "", opens_at: "", closes_at: "", max_registrations: "",
  custom_title: "", custom_description: "", custom_school_id: "", custom_start_date: "", custom_end_date: "",
};

export function AdminRegistrationsList() {
  const [forms, setForms] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [schools, setSchools] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [f, b, s, p] = await Promise.all([
        api.get("/registrations/forms"),
        api.get("/bookings"),
        api.get("/schools"),
        api.get("/programs"),
      ]);
      setForms(f.data || []);
      setBookings((b.data || []).filter((bk) => bk.status === "approved"));
      setSchools(s.data || []);
      setPrograms(p.data || []);
    } catch { toast.error("Could not load registration forms"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(EMPTY_FORM); setCreating(true); };

  const submit = async () => {
    if (form.training_type === "custom") {
      if (!form.custom_title || !form.custom_school_id) { toast.error("Enter a training title and select a school"); return; }
    } else {
      if (!form.booking_id) { toast.error("Select an approved booking"); return; }
      const b = bookings.find((bk) => bk.id === form.booking_id);
      const p = b && programs.find((pg) => pg.id === b.program_id);
      if (!p) { toast.error("This booking's course could not be found"); return; }
    }
    if (!form.opens_at || !form.closes_at) {
      toast.error("Set both a registration open date and close date");
      return;
    }
    if (form.closes_at < form.opens_at) {
      toast.error("Registration close date must be after the open date");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, max_registrations: form.max_registrations ? parseInt(form.max_registrations) : null };
      await api.post("/registrations/forms", payload);
      toast.success("Registration form created");
      setCreating(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create form");
    } finally { setSaving(false); }
  };

  const deleteForm = async (e, formId) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm("Delete this registration form? The link will stop working. This cannot be undone.")) return;
    try {
      await api.delete(`/registrations/forms/${formId}`);
      toast.success("Registration form deleted");
      load();
    } catch { toast.error("Failed to delete"); }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
      <Header
        title="Teacher Registration Forms"
        subtitle={`${forms.length} form${forms.length === 1 ? "" : "s"}`}
        actions={
          <button
            onClick={openNew}
            data-testid="reg-new-form"
            className="inline-flex items-center gap-1.5 bg-[#0F172A] text-white text-sm font-medium px-3.5 py-2 rounded-lg hover:bg-[#0F172A]/90 transition-colors"
          >
            <Plus className="w-4 h-4" /> New registration form
          </button>
        }
      />

      {loading ? (
        <div className="mt-6 space-y-3">{[0, 1, 2].map((i) => <div key={i} className="h-20 rounded-xl bg-gray-100 animate-pulse" />)}</div>
      ) : forms.length === 0 ? (
        <div className="mt-10 rounded-2xl border border-dashed border-gray-200 bg-white p-12 text-center">
          <div className="font-display text-lg font-medium text-[#0F172A]">No registration forms yet</div>
          <p className="mt-1 text-sm text-gray-500">Create one for an approved booking to start collecting teacher registrations.</p>
        </div>
      ) : (
        <div className="mt-6 space-y-3">
          {forms.map((f) => (
            <Link
              key={f.id}
              to={`/admin/registrations/${f.id}`}
              data-testid={`reg-form-row-${f.id}`}
              className="flex items-center gap-4 rounded-xl border border-gray-200 bg-white p-4 hover:border-gray-300 transition-colors"
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="font-medium text-[#0F172A]">{f.program_title || "Training"}</h3>
                  <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide ${f.training_type === "custom" ? "bg-indigo-100 text-indigo-700" : "bg-gray-100 text-gray-600"}`}>
                    {f.training_type === "custom" ? "Custom" : "Standard"}
                  </span>
                </div>
                <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-gray-500">
                  <span>{f.school_name}</span>
                  <span className="text-gray-300">·</span>
                  <span>{f.training_date}</span>
                  <span className="text-gray-300">·</span>
                  <span className={`px-1.5 py-0.5 rounded-full font-medium ${f.status === "open" ? "bg-emerald-100 text-emerald-700" : f.status === "closed" ? "bg-gray-100 text-gray-600" : "bg-amber-100 text-amber-700"}`}>
                    {f.status}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-1.5 text-sm text-gray-500 shrink-0">
                <Users className="w-4 h-4" /> {f.submission_count}
              </div>
              <button
                onClick={(e) => deleteForm(e, f.id)}
                data-testid={`reg-form-delete-${f.id}`}
                className="w-8 h-8 grid place-items-center rounded-lg hover:bg-rose-50 text-gray-400 hover:text-rose-600 shrink-0"
                aria-label="Delete form"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </Link>
          ))}
        </div>
      )}

      {creating && (
        <AdminModal
          onClose={() => setCreating(false)}
          maxWidth="max-w-lg"
          title="New registration form"
          subtitle={form.training_type === "custom"
            ? "A one-off training for a single school. Not added to the public catalog unless published later."
            : "Select a booking — everything else is filled in for you."}
          footer={
            <>
              <button onClick={() => setCreating(false)} className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg w-full sm:w-auto">
                Cancel
              </button>
              <button
                onClick={submit}
                disabled={saving}
                data-testid="reg-form-save"
                className="bg-[#0F172A] disabled:bg-gray-400 text-white text-sm font-medium px-4 py-2.5 rounded-lg hover:bg-[#0F172A]/90 transition-colors inline-flex items-center justify-center gap-1.5 w-full sm:w-auto"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                {saving ? "Creating…" : "Create Registration Form"}
              </button>
            </>
          }
        >
            {/* Training type toggle */}
            <div className="flex gap-2 mb-4">
              {[["standard", "Standard Training"], ["custom", "Custom School Training"]].map(([val, label]) => (
                <button
                  key={val}
                  onClick={() => setForm((f) => ({ ...f, training_type: val }))}
                  data-testid={`reg-type-${val}`}
                  className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                    form.training_type === val ? "border-[#0F172A] bg-[#0F172A] text-white" : "border-gray-200 text-gray-600 hover:border-gray-300"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            <div className="grid gap-4">
              {form.training_type === "standard" ? (
                <>
                  <select
                    value={form.booking_id}
                    onChange={(e) => setForm({ ...form, booking_id: e.target.value })}
                    data-testid="reg-field-booking"
                    className="px-3 py-2.5 rounded-lg border border-gray-200 outline-none text-sm bg-white focus:border-[#4F46E5] focus:ring-2 focus:ring-[#4F46E5]/10"
                  >
                    <option value="">Select an approved booking…</option>
                    {bookings.map((b) => {
                      const p = programs.find((pg) => pg.id === b.program_id);
                      return (
                        <option key={b.id} value={b.id}>
                          {b.school_name} · {p?.title || "Unknown course"} · {b.preferred_date}
                        </option>
                      );
                    })}
                  </select>

                  {/* Section 1 — read-only Training Information, matching the existing card style */}
                  {form.booking_id && (() => {
                    const b = bookings.find((bk) => bk.id === form.booking_id);
                    const p = b && programs.find((pg) => pg.id === b.program_id);
                    if (!b) return null;
                    if (!p) {
                      return (
                        <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700" data-testid="reg-booking-course-missing">
                          This booking's course could not be found, so a registration form can't be created for it. Check the Programs list, or choose a different booking.
                        </div>
                      );
                    }
                    return (
                      <div className="rounded-xl border border-gray-200 bg-gray-50 p-4" data-testid="reg-booking-preview">
                        <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Training Information</div>
                        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2.5 text-sm">
                          <div><dt className="text-xs text-gray-500">School</dt><dd className="font-medium text-[#0F172A]">{b.school_name}</dd></div>
                          <div><dt className="text-xs text-gray-500">Course</dt><dd className="font-medium text-[#0F172A]">{p.title}</dd></div>
                          <div><dt className="text-xs text-gray-500">Training Date</dt><dd className="font-medium text-[#0F172A]">{b.preferred_date}</dd></div>
                          <div><dt className="text-xs text-gray-500">Training Duration</dt><dd className="font-medium text-[#0F172A]">{p.duration_hours} hours{form.training_duration_hours > 0 && form.training_duration_hours !== p.duration_hours ? <span className="ml-1 text-xs text-amber-600">(registered: {form.training_duration_hours}h)</span> : ""}</dd></div>
                          <div><dt className="text-xs text-gray-500">Booking Status</dt><dd><StatusBadge status="approved" /></dd></div>
                          <div>
                            <dt className="text-xs text-gray-500">Trainer</dt>
                            <input
                              value={form.trainer_name}
                              onChange={(e) => setForm({ ...form, trainer_name: e.target.value })}
                              placeholder="Not tracked on the booking — optional"
                              data-testid="reg-field-trainer-standard"
                              className="mt-0.5 w-full px-0 py-0 border-0 border-b border-dashed border-gray-300 bg-transparent outline-none text-sm font-medium text-[#0F172A] placeholder:text-gray-400 placeholder:font-normal focus:border-[#4F46E5]"
                            />
                          </div>
                        </dl>
                      </div>
                    );
                  })()}
                </>
              ) : (
                <>
                  <input
                    placeholder="Custom training title*"
                    value={form.custom_title}
                    onChange={(e) => setForm({ ...form, custom_title: e.target.value })}
                    data-testid="reg-field-custom-title"
                    className="px-3 py-2.5 rounded-lg border border-gray-200 outline-none text-sm focus:border-[#4F46E5] focus:ring-2 focus:ring-[#4F46E5]/10"
                  />
                  <select
                    value={form.custom_school_id}
                    onChange={(e) => setForm({ ...form, custom_school_id: e.target.value })}
                    data-testid="reg-field-custom-school"
                    className="px-3 py-2.5 rounded-lg border border-gray-200 outline-none text-sm bg-white focus:border-[#4F46E5] focus:ring-2 focus:ring-[#4F46E5]/10"
                  >
                    <option value="">Select school*…</option>
                    {schools.map((s) => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                  <textarea
                    placeholder="Short description (optional)"
                    value={form.custom_description}
                    onChange={(e) => setForm({ ...form, custom_description: e.target.value })}
                    rows={2}
                    className="px-3 py-2.5 rounded-lg border border-gray-200 outline-none text-sm focus:border-[#4F46E5] focus:ring-2 focus:ring-[#4F46E5]/10 resize-none"
                  />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                    <input
                      placeholder="Trainer"
                      value={form.trainer_name}
                      onChange={(e) => setForm({ ...form, trainer_name: e.target.value })}
                      data-testid="reg-field-trainer-custom"
                      className="px-3 py-2.5 rounded-lg border border-gray-200 outline-none text-sm focus:border-[#4F46E5] focus:ring-2 focus:ring-[#4F46E5]/10"
                    />
                    <input
                      placeholder="Training topic"
                      value={form.training_topic}
                      onChange={(e) => setForm({ ...form, training_topic: e.target.value })}
                      data-testid="reg-field-topic-custom"
                      className="px-3 py-2.5 rounded-lg border border-gray-200 outline-none text-sm focus:border-[#4F46E5] focus:ring-2 focus:ring-[#4F46E5]/10"
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                    <label className="block">
                      <span className="text-xs font-medium text-gray-600 mb-1 block">Training start date</span>
                      <input
                        type="date"
                        value={form.custom_start_date}
                        onChange={(e) => setForm({ ...form, custom_start_date: e.target.value })}
                        data-testid="reg-field-custom-start"
                        className="w-full px-3 py-2.5 rounded-lg border border-gray-200 outline-none text-sm focus:border-[#4F46E5] focus:ring-2 focus:ring-[#4F46E5]/10"
                      />
                    </label>
                    <label className="block">
                      <span className="text-xs font-medium text-gray-600 mb-1 block">Training end date</span>
                      <input
                        type="date"
                        value={form.custom_end_date}
                        onChange={(e) => setForm({ ...form, custom_end_date: e.target.value })}
                        data-testid="reg-field-custom-end"
                        className="w-full px-3 py-2.5 rounded-lg border border-gray-200 outline-none text-sm focus:border-[#4F46E5] focus:ring-2 focus:ring-[#4F46E5]/10"
                      />
                    </label>
                  </div>
                </>
              )}

              {/* Section 2 — Registration Settings only */}
              <div className="pt-1 border-t border-gray-100">
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mt-3 mb-2.5">Registration Settings</div>
                <div className="grid gap-2.5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                    <label className="block">
                      <span className="text-xs font-medium text-gray-600 mb-1 block">Registration opens on*</span>
                      <input
                        type="date"
                        value={form.opens_at ? form.opens_at.slice(0, 10) : ""}
                        onChange={(e) => setForm({ ...form, opens_at: toLocalMidnightISO(e.target.value) })}
                        data-testid="reg-field-opens"
                        className="w-full px-3 py-2.5 rounded-lg border border-gray-200 outline-none text-sm focus:border-[#4F46E5] focus:ring-2 focus:ring-[#4F46E5]/10"
                      />
                    </label>
                    <label className="block">
                      <span className="text-xs font-medium text-gray-600 mb-1 block">Registration closes on*</span>
                      <input
                        type="date"
                        value={form.closes_at ? form.closes_at.slice(0, 10) : ""}
                        onChange={(e) => setForm({ ...form, closes_at: toLocalMidnightISO(e.target.value) })}
                        data-testid="reg-field-closes"
                        className="w-full px-3 py-2.5 rounded-lg border border-gray-200 outline-none text-sm focus:border-[#4F46E5] focus:ring-2 focus:ring-[#4F46E5]/10"
                      />
                    </label>
                  </div>
                  <input
                    type="number"
                    placeholder="Maximum registrations (optional)"
                    value={form.max_registrations}
                    onChange={(e) => setForm({ ...form, max_registrations: e.target.value })}
                    data-testid="reg-field-max"
                    className="px-3 py-2.5 rounded-lg border border-gray-200 outline-none text-sm focus:border-[#4F46E5] focus:ring-2 focus:ring-[#4F46E5]/10"
                  />
                </div>
              </div>
            </div>
        </AdminModal>
      )}
    </motion.div>
  );
}

// ── Default review questions ─────────────────────────────────────────────────
const DEFAULT_REVIEW_QUESTIONS = [
  { id: "q1", text: "Overall Rating", type: "star_rating", required: true, active: true, order: 1 },
  { id: "q2", text: "How would you rate the trainer?", type: "star_rating", required: true, active: true, order: 2 },
  { id: "q3", text: "How relevant was the training?", type: "dropdown", required: true, active: true, order: 3, options: ["Excellent","Very Good","Good","Average","Poor"] },
  { id: "q4", text: "How useful was the training material?", type: "dropdown", required: true, active: true, order: 4, options: ["Excellent","Very Good","Good","Average","Poor"] },
  { id: "q5", text: "Would you recommend this training?", type: "radio", required: true, active: true, order: 5, options: ["Yes","No","Maybe"] },
  { id: "q6", text: "What did you like most?", type: "paragraph", required: false, active: true, order: 6 },
  { id: "q7", text: "Suggestions for improvement", type: "paragraph", required: false, active: true, order: 7 },
];

const Q_TYPES = [
  { value: "star_rating", label: "★ Star Rating" },
  { value: "dropdown", label: "Dropdown" },
  { value: "radio", label: "Radio Buttons" },
  { value: "checkbox", label: "Checkbox" },
  { value: "short_answer", label: "Short Answer" },
  { value: "paragraph", label: "Paragraph" },
];

function ReviewConfig({ formId, form, onSave }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(form.review_enabled ?? true);
  const [mandatory, setMandatory] = useState(form.review_mandatory ?? true);
  const [title, setTitle] = useState(form.review_title || "Training Feedback");
  const [desc, setDesc] = useState(form.review_description || "Please help us improve our future training programmes by sharing your experience.");
  const [questions, setQuestions] = useState(form.review_questions?.length ? form.review_questions : DEFAULT_REVIEW_QUESTIONS);
  const [editingId, setEditingId] = useState(null);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/registrations/forms/${formId}`, {
        review_enabled: enabled,
        review_mandatory: mandatory,
        review_title: title,
        review_description: desc,
        review_questions: questions,
      });
      toast.success("Review configuration saved");
      onSave();
    } catch { toast.error("Could not save review config"); }
    finally { setSaving(false); }
  };

  const addQuestion = () => {
    const newQ = { id: `q${Date.now()}`, text: "", type: "short_answer", required: false, active: true, order: questions.length + 1, options: [] };
    setQuestions([...questions, newQ]);
    setEditingId(newQ.id);
  };

  const updateQ = (id, patch) => setQuestions(questions.map((q) => q.id === id ? { ...q, ...patch } : q));
  const removeQ = (id) => setQuestions(questions.filter((q) => q.id !== id));
  const moveQ = (idx, dir) => {
    const arr = [...questions];
    const target = idx + dir;
    if (target < 0 || target >= arr.length) return;
    [arr[idx], arr[target]] = [arr[target], arr[idx]];
    setQuestions(arr.map((q, i) => ({ ...q, order: i + 1 })));
  };

  const inp = "w-full px-3 py-2 rounded-lg border border-gray-200 outline-none text-sm focus:border-[#4F46E5] focus:ring-2 focus:ring-[#4F46E5]/10";

  return (
    <div className="mt-4 rounded-xl border border-gray-200 bg-white overflow-hidden">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-gray-50 transition-colors">
        <div className="flex items-center gap-2">
          <MessageCircleQuestion className="w-4 h-4 text-[#4F46E5]" />
          <span className="text-sm font-semibold text-[#0F172A]">Review Form Configuration</span>
          {enabled && <span className="px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-emerald-100 text-emerald-700">Active</span>}
        </div>
        <ChevronRight className={`w-4 h-4 text-gray-400 transition-transform ${open ? "rotate-90" : ""}`} />
      </button>

      {open && (
        <div className="border-t border-gray-100 p-4 space-y-4">
          {/* Toggles */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} className="accent-[#4F46E5]" />
              <span className="text-sm text-gray-700">Enable Review Form</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={mandatory} onChange={(e) => setMandatory(e.target.checked)} className="accent-[#4F46E5]" />
              <span className="text-sm text-gray-700">Review Mandatory</span>
            </label>
          </div>

          {/* Title & Description */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs font-medium text-gray-600 mb-1 block">Review Title</span>
              <input className={inp} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Training Feedback" />
            </label>
            <label className="block">
              <span className="text-xs font-medium text-gray-600 mb-1 block">Description</span>
              <input className={inp} value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Help us improve..." />
            </label>
          </div>

          {/* Questions */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Questions ({questions.filter((q) => q.active).length} active)</span>
              <button onClick={addQuestion} className="inline-flex items-center gap-1 text-xs font-medium text-[#4F46E5] hover:underline"><Plus className="w-3 h-3" /> Add question</button>
            </div>
            <div className="space-y-2">
              {questions.map((q, idx) => (
                <div key={q.id} className={`rounded-lg border p-3 ${q.active ? "border-gray-200 bg-white" : "border-gray-100 bg-gray-50 opacity-60"}`}>
                  <div className="flex items-start gap-2">
                    <div className="flex flex-col gap-1 pt-1">
                      <button onClick={() => moveQ(idx, -1)} disabled={idx === 0} className="text-gray-400 hover:text-gray-600 disabled:opacity-30"><ChevronLeft className="w-3 h-3 rotate-90" /></button>
                      <button onClick={() => moveQ(idx, 1)} disabled={idx === questions.length - 1} className="text-gray-400 hover:text-gray-600 disabled:opacity-30"><ChevronLeft className="w-3 h-3 -rotate-90" /></button>
                    </div>
                    <div className="flex-1 min-w-0">
                      {editingId === q.id ? (
                        <div className="space-y-2">
                          <input className={inp} value={q.text} onChange={(e) => updateQ(q.id, { text: e.target.value })} placeholder="Question text" autoFocus />
                          <div className="grid grid-cols-2 gap-2">
                            <select className={inp} value={q.type} onChange={(e) => updateQ(q.id, { type: e.target.value })}>
                              {Q_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                            </select>
                            <label className="flex items-center gap-2">
                              <input type="checkbox" checked={q.required} onChange={(e) => updateQ(q.id, { required: e.target.checked })} className="accent-[#4F46E5]" />
                              <span className="text-xs text-gray-600">Required</span>
                            </label>
                          </div>
                          {(q.type === "dropdown" || q.type === "radio" || q.type === "checkbox") && (
                            <input className={inp} value={(q.options || []).join(", ")} onChange={(e) => updateQ(q.id, { options: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} placeholder="Options (comma separated)" />
                          )}
                          <button onClick={() => setEditingId(null)} className="text-xs text-[#4F46E5] hover:underline">Done</button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 cursor-pointer" onClick={() => setEditingId(q.id)}>
                          <span className="text-sm text-[#0F172A]">{q.text || "Untitled question"}</span>
                          <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-500">{Q_TYPES.find((t) => t.value === q.type)?.label || q.type}</span>
                          {q.required && <span className="text-red-400 text-xs">*</span>}
                        </div>
                      )}
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <button onClick={() => updateQ(q.id, { active: !q.active })} className="w-7 h-7 grid place-items-center rounded hover:bg-gray-100 text-gray-400">
                        {q.active ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> : <XCircle className="w-3.5 h-3.5" />}
                      </button>
                      <button onClick={() => removeQ(q.id)} className="w-7 h-7 grid place-items-center rounded hover:bg-rose-50 text-gray-400 hover:text-rose-500">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <button onClick={save} disabled={saving} className="inline-flex items-center gap-1.5 bg-[#0F172A] text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-[#1e293b] disabled:opacity-50">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
            {saving ? "Saving…" : "Save review config"}
          </button>
        </div>
      )}
    </div>
  );
}

export function AdminRegistrationDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [form, setForm] = useState(null);
  const [subs, setSubs] = useState([]);
  const [stats, setStats] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [busy, setBusy] = useState(false);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [changesTarget, setChangesTarget] = useState(null);
  const [changesNote, setChangesNote] = useState("");
  const [kpiModal, setKpiModal] = useState(null);   // { label } of the open KPI, or null
  const [kpiRows, setKpiRows] = useState([]);
  const [kpiLoading, setKpiLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      const [f, s, st] = await Promise.all([
        api.get(`/registrations/forms/${id}`),
        api.get(`/registrations/forms/${id}/submissions`, { params: statusFilter === "all" ? {} : { status: statusFilter } }),
        api.get("/registrations/stats", { params: { form_id: id } }),
      ]);
      setForm(f.data);
      setSubs(s.data || []);
      setStats(st.data);
    } catch {
      toast.error("Could not load registration form");
      nav("/admin/registrations");
    }
  }, [id, statusFilter, nav]);
  useEffect(() => { load(); }, [load]);

  const registrationUrl = form ? `${window.location.origin}/register-training/${form.token}` : "";

  const copyLink = () => {
    navigator.clipboard?.writeText(registrationUrl);
    toast.success("Link copied");
  };

  const regenerateLink = async () => {
    if (!window.confirm("Regenerate the link? The old link will stop working immediately.")) return;
    setBusy(true);
    try {
      await api.post(`/registrations/forms/${id}/regenerate-link`);
      toast.success("Link regenerated");
      load();
    } catch { toast.error("Could not regenerate link"); }
    finally { setBusy(false); }
  };

  const setFormStatus = async (status) => {
    setBusy(true);
    try {
      await api.patch(`/registrations/forms/${id}`, { status });
      toast.success(`Registration ${status}`);
      load();
    } catch { toast.error("Could not update status"); }
    finally { setBusy(false); }
  };

  const approve = async (sid) => {
    if (!window.confirm("Approve this registration? A certificate will be generated and emailed.")) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/registrations/submissions/${sid}/approve`);
      toast.success(`Approved · Certificate ${data.certificate_id}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not approve"); }
    finally { setBusy(false); }
  };

  const reject = async () => {
    if (!rejectReason.trim()) { toast.error("Please add a rejection reason"); return; }
    setBusy(true);
    try {
      await api.post(`/registrations/submissions/${rejectTarget}/reject`, { reason: rejectReason });
      toast.success("Rejected · teacher notified");
      setRejectTarget(null); setRejectReason("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not reject"); }
    finally { setBusy(false); }
  };

  const requestChanges = async () => {
    setBusy(true);
    try {
      await api.post(`/registrations/submissions/${changesTarget}/request-changes`, { note: changesNote });
      toast.success("Change request sent");
      setChangesTarget(null); setChangesNote("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send request"); }
    finally { setBusy(false); }
  };

  // KPI card click → the exact teachers behind that count. Reuses the same submissions
  // endpoint the page already loads from (scoped to this form), just with the filter that
  // matches the clicked KPI — so the list can never drift from the number shown on the card.
  const openKpi = async (label, params) => {
    setKpiModal({ label });
    setKpiLoading(true);
    setKpiRows([]);
    try {
      const { data } = await api.get(`/registrations/forms/${id}/submissions`, { params });
      setKpiRows(data || []);
    } catch {
      toast.error("Could not load teachers for this KPI");
    } finally {
      setKpiLoading(false);
    }
  };

  const removeSubmission = async (sid) => {
    if (!window.confirm("Delete this registration submission? This cannot be undone.")) return;
    setBusy(true);
    try {
      await api.delete(`/registrations/submissions/${sid}`);
      toast.success("Submission deleted");
      load();
    } catch { toast.error("Failed to delete"); }
    finally { setBusy(false); }
  };

  if (!form) return <div className="p-8 text-gray-500">Loading…</div>;

  return (
    <div className="fade-up" data-testid="registration-detail">
      <div className="flex items-center gap-2 mb-4">
        <Link to="/admin/registrations" className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-[#0F172A]">
          <ArrowLeft className="w-3.5 h-3.5" /> All registration forms
        </Link>
      </div>

      <Header
        title={form.program_title || "Training registration"}
        subtitle={`${form.school_name} · ${form.training_date}`}
      />

      {/* Link management */}
      <div className="mt-6 rounded-xl border border-gray-200 bg-white p-4">
        <div className="flex items-center gap-2 text-xs font-medium text-gray-600 mb-2"><LinkIcon className="w-3.5 h-3.5" /> Registration link</div>
        <div className="flex flex-wrap items-center gap-2">
          <code className="flex-1 min-w-[200px] px-3 py-2 rounded-lg bg-gray-50 border border-gray-200 text-xs text-gray-700 truncate">{registrationUrl}</code>
          <button onClick={copyLink} className="inline-flex items-center gap-1.5 border border-gray-200 hover:border-gray-300 text-sm px-3 py-2 rounded-lg">
            <Copy className="w-3.5 h-3.5" /> Copy
          </button>
          <button onClick={regenerateLink} disabled={busy} className="inline-flex items-center gap-1.5 border border-gray-200 hover:border-gray-300 text-sm px-3 py-2 rounded-lg disabled:opacity-40">
            <RefreshCw className="w-3.5 h-3.5" /> Regenerate
          </button>
          {form.status === "open" ? (
            <button onClick={() => setFormStatus("closed")} disabled={busy} className="text-sm px-3 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 disabled:opacity-40">Close registration</button>
          ) : (
            <button onClick={() => setFormStatus("open")} disabled={busy} className="text-sm px-3 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40">
              {form.status === "draft" ? "Open registration" : "Reopen registration"}
            </button>
          )}
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-5 gap-3">
          {[
            ["Pending", stats.pending, Clock, { status: "pending" }],
            ["Approved", stats.approved, CheckCircle2, { status: "approved" }],
            ["Rejected", stats.rejected, XCircle, { status: "rejected" }],
            ["Changes requested", stats.changes_requested, MessageCircleQuestion, { status: "changes_requested" }],
            ["Certificates issued", stats.certificates_issued, Award, { has_certificate: true }],
          ].map(([label, val, Icon, params]) => (
            <button
              key={label}
              type="button"
              onClick={() => openKpi(label, params)}
              data-testid={`reg-kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}
              className="text-left rounded-xl border border-gray-200 bg-white p-3 hover:border-[#4F46E5]/40 hover:shadow-sm transition-all cursor-pointer"
            >
              <Icon className="w-4 h-4 text-gray-400 mb-1.5" />
              <div className="text-lg font-semibold text-[#0F172A]">{val}</div>
              <div className="text-[11px] text-gray-500">{label}</div>
            </button>
          ))}
        </div>
      )}

      {/* KPI teacher list */}
      {kpiModal && (
        <AdminModal
          onClose={() => setKpiModal(null)}
          title={kpiModal.label}
          subtitle={kpiLoading ? "Loading…" : `${kpiRows.length} teacher${kpiRows.length === 1 ? "" : "s"}`}
          testId="reg-kpi-modal"
          maxWidth="max-w-lg"
        >
          {kpiLoading ? (
            <div className="space-y-2">{[0, 1, 2].map((i) => <div key={i} className="h-14 rounded-lg bg-gray-100 animate-pulse" />)}</div>
          ) : kpiRows.length === 0 ? (
            <div className="py-8 text-center text-sm text-gray-500">No teachers in this category.</div>
          ) : (
            <div className="space-y-2">
              {kpiRows.map((r) => (
                <div key={r.id} className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2.5" data-testid={`reg-kpi-row-${r.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-medium text-sm text-[#0F172A] truncate">{r.full_name}</div>
                    <StatusBadge status={r.status} />
                  </div>
                  <div className="mt-0.5 text-xs text-gray-500 flex flex-wrap gap-x-3">
                    <span>{r.email}</span>
                    <span>{fmtDate(r.submitted_at)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </AdminModal>
      )}

      {/* ── Review Form Configuration ────────────────────────────────── */}
      <ReviewConfig formId={id} form={form} onSave={load} />

      {/* Filter */}
      <div className="mt-6 flex gap-2">
        {["all", "pending", "approved", "rejected", "changes_requested"].map((s) => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium capitalize transition-all ${statusFilter === s ? "bg-[#0F172A] text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
          >
            {s.replace("_", " ")}
          </button>
        ))}
      </div>

      {/* Submissions */}
      {subs.length === 0 ? (
        <div className="mt-4 rounded-2xl border border-dashed border-gray-200 bg-white p-12 text-center text-sm text-gray-500">
          No registrations yet.
        </div>
      ) : (
        <div className="mt-4 rounded-2xl border border-gray-200 bg-white overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold whitespace-nowrap">Submitted</th>
                  <th className="text-left px-4 py-3 font-semibold">Participant</th>
                  <th className="text-left px-4 py-3 font-semibold">Program · Trainer</th>
                  <th className="text-left px-4 py-3 font-semibold">School</th>
                  <th className="text-left px-4 py-3 font-semibold">Complete</th>
                  <th className="text-left px-4 py-3 font-semibold">Rating</th>
                  <th className="text-left px-4 py-3 font-semibold">Status</th>
                  <th className="text-left px-4 py-3 font-semibold">Cert</th>
                  <th className="text-left px-4 py-3 font-semibold">Email</th>
                  <th className="text-right px-4 py-3 font-semibold">Action</th>
                </tr>
              </thead>
              <tbody>
                {subs.map((s) => {
                  // Existing consent fields already collected on every submission — the only
                  // "completeness" signal Teacher Registration already has (no completion_percent
                  // field exists for this collection, unlike feedback_submissions).
                  const consents = [s.consent_info_correct, s.consent_certificate, s.consent_email];
                  const done = consents.filter(Boolean).length;
                  return (
                    <tr key={s.id} className="border-t border-gray-100" data-testid={`reg-sub-${s.id}`}>
                      <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">{fmtDate(s.submitted_at)}</td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-[#0F172A]">{s.full_name}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-[#0F172A]">{form.program_title || "—"}</div>
                        <div className="text-xs text-gray-500">{form.trainer_name || "—"}</div>
                      </td>
                      <td className="px-4 py-3 text-gray-600">{s.school_name}</td>
                      <td className="px-4 py-3">
                        <div className="w-20 bg-gray-100 rounded-full h-1.5 overflow-hidden">
                          <div className="h-full bg-emerald-500" style={{ width: `${(done / 3) * 100}%` }} />
                        </div>
                        <div className="text-[10px] text-gray-500 mt-0.5">{done}/3</div>
                      </td>
                      <td className="px-4 py-3">
                        {s.review_rating ? (
                          <span className="inline-flex items-center gap-0.5 text-xs" data-testid={`reg-sub-rating-${s.id}`}>
                            <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" /> {s.review_rating}/5
                          </span>
                        ) : <span className="text-gray-300">—</span>}
                      </td>
                      <td className="px-4 py-3"><StatusBadge status={s.status} /></td>
                      <td className="px-4 py-3">
                        {s.certificate_id ? (
                          <a
                            href={`${api.defaults.baseURL}/certificates/${s.certificate_id}/pdf`}
                            target="_blank" rel="noreferrer"
                            className="inline-flex items-center gap-1 text-xs text-[#4F46E5] hover:underline"
                          >
                            <Award className="w-3.5 h-3.5" /> {s.certificate_id}
                          </a>
                        ) : <span className="text-gray-300">—</span>}
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-600">{s.email}</td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap items-center justify-end gap-1.5">
                          {s.status !== "approved" && s.status !== "rejected" && (
                            <>
                              <button onClick={() => approve(s.id)} disabled={busy} className="text-xs font-medium bg-emerald-600 text-white px-3 py-1.5 rounded-lg hover:bg-emerald-700 disabled:opacity-40">
                                Approve
                              </button>
                              <button onClick={() => { setChangesTarget(s.id); setChangesNote(""); }} className="text-xs font-medium bg-amber-100 text-amber-700 px-3 py-1.5 rounded-lg hover:bg-amber-200">
                                Request changes
                              </button>
                              <button onClick={() => { setRejectTarget(s.id); setRejectReason(""); }} className="text-xs font-medium bg-rose-100 text-rose-700 px-3 py-1.5 rounded-lg hover:bg-rose-200">
                                Reject
                              </button>
                            </>
                          )}
                          <button
                            onClick={() => removeSubmission(s.id)}
                            disabled={busy}
                            data-testid={`reg-sub-delete-${s.id}`}
                            className="w-8 h-8 grid place-items-center rounded-lg hover:bg-rose-50 text-gray-400 hover:text-rose-600 disabled:opacity-40"
                            aria-label="Delete submission"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Reject modal */}
      {rejectTarget && (
        <AdminModal
          onClose={() => setRejectTarget(null)}
          title="Reject registration"
          testId="reg-reject-modal"
          footer={
            <>
              <button onClick={() => setRejectTarget(null)} className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg w-full sm:w-auto">Cancel</button>
              <button onClick={reject} disabled={busy} className="px-4 py-2 text-sm font-medium bg-rose-600 text-white rounded-lg hover:bg-rose-700 disabled:opacity-50 w-full sm:w-auto">Reject</button>
            </>
          }
        >
          <textarea
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="Reason (shared with the teacher)"
            rows={3}
            className="w-full px-3 py-2.5 rounded-lg border border-gray-200 outline-none text-sm focus:border-[#4F46E5] focus:ring-2 focus:ring-[#4F46E5]/10 resize-none"
          />
        </AdminModal>
      )}

      {/* Request changes modal */}
      {changesTarget && (
        <AdminModal
          onClose={() => setChangesTarget(null)}
          title="Request changes"
          testId="reg-changes-modal"
          footer={
            <>
              <button onClick={() => setChangesTarget(null)} className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg w-full sm:w-auto">Cancel</button>
              <button onClick={requestChanges} disabled={busy} className="px-4 py-2 text-sm font-medium bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-50 w-full sm:w-auto">Send request</button>
            </>
          }
        >
          <textarea
            value={changesNote}
            onChange={(e) => setChangesNote(e.target.value)}
            placeholder="What should the teacher fix?"
            rows={3}
            className="w-full px-3 py-2.5 rounded-lg border border-gray-200 outline-none text-sm focus:border-[#4F46E5] focus:ring-2 focus:ring-[#4F46E5]/10 resize-none"
          />
        </AdminModal>
      )}
    </div>
  );
}